import { createAppRoute } from "@/app/router/utils";
import { Uuid } from "@/domains/global/identifiers";

export const atlasRoutes = {
  root: createAppRoute(`/`),
  logIn: createAppRoute(`/login`),
  logInEmailPassword: createAppRoute(`/login/email-password`),
  logOut: createAppRoute(`/logout`),
  googleOAuthRedirect: createAppRoute(`/google-oauth-redirect`),

  /**
   * Core Routes
   */
  home: createAppRoute(`/home`),
  settings: createAppRoute(`/settings`),

  /**
   * Company Routes
   */
  company: createAppRoute(`/company`),
  companyTeam: createAppRoute(`/company/team`),
  companyLinks: createAppRoute(`/company/links`),

  /**
   * Note Routes
   */
  notes: createAppRoute(`/notes`),
  notesCreate: createAppRoute(`/notes/create`),
  notesView: createAppRoute<{ noteId: Uuid }>(({ noteId }) => `/notes/${noteId}`),
  notesSnapshots: createAppRoute<{ noteId: Uuid }>(({ noteId }) => `/notes/${noteId}/snapshots`),
  notesUploadAudio: createAppRoute<{ noteId: Uuid }>(
    ({ noteId }) => `/notes/${noteId}/upload-audio`
  ),

  /**
   * Collection Routes
   */
  collections: createAppRoute(`/collections`),
  collectionsView: createAppRoute<{ collectionId: Uuid }>(
    ({ collectionId }) => `/collections/${collectionId}`
  ),

  /**
   * Account Routes
   */
  accounts: createAppRoute(`/accounts`),
  accountsView: createAppRoute<{ accountId: Uuid }>(({ accountId }) => `/accounts/${accountId}`),
  accountImportFromMem1Dot0: createAppRoute<{ accountId: Uuid }>(
    ({ accountId }) => `/accounts/${accountId}/import-from-mem-1-dot-0`
  ),
  accountMigrateEmailAddress: createAppRoute<{ accountId: Uuid }>(
    ({ accountId }) => `/accounts/${accountId}/migrate-email-address`
  ),

  /**
   * Space Account Routes
   */
  spaceAccounts: createAppRoute(`/space-accounts`),
  spaceAccountsView: createAppRoute<{ spaceAccountId: Uuid }>(
    ({ spaceAccountId }) => `/space-accounts/${spaceAccountId}`
  ),
  spaceAccountSyncClientGroups: createAppRoute<{ spaceAccountId: Uuid }>(
    ({ spaceAccountId }) => `/space-accounts/${spaceAccountId}/sync-client-groups`
  ),
  spaceAccountDataImports: createAppRoute<{ spaceAccountId: Uuid }>(
    ({ spaceAccountId }) => `/space-accounts/${spaceAccountId}/data-imports`
  ),
  spaceAccountSpaceAccountAppCallouts: createAppRoute<{ spaceAccountId: Uuid }>(
    ({ spaceAccountId }) => `/space-accounts/${spaceAccountId}/space-account-app-callouts`
  ),

  /**
   * Legacy Firestore User Routes
   */
  legacyFirestoreUsers: createAppRoute(`/legacy-firestore-users`),
  legacyFirestoreUsersView: createAppRoute<{ legacyFirestoreUserId: Uuid }>(
    ({ legacyFirestoreUserId }) => `/legacy-firestore-users/${legacyFirestoreUserId}`
  ),

  /**
   * Session Routes
   */
  sessions: createAppRoute(`/sessions`),
  sessionsCreate: createAppRoute(`/sessions/create`),
  sessionsView: createAppRoute<{ sessionId: Uuid }>(({ sessionId }) => `/sessions/${sessionId}`),
  sessionEvents: createAppRoute<{ sessionId: Uuid }>(
    ({ sessionId }) => `/sessions/${sessionId}/session-events`
  ),
  sessionArtifactsView: createAppRoute<{ artifactId: Uuid }>(
    ({ artifactId }) => `/sessions/artifacts/${artifactId}`
  ),
  sessionOperationsView: createAppRoute<{ operationId: Uuid }>(
    ({ operationId }) => `/sessions/operations/${operationId}`
  ),

  /**
   * Assistant Thread Routes
   */
  assistants: createAppRoute(`/assistants`),
  assistantsView: createAppRoute<{ sessionId: Uuid }>(
    ({ sessionId }) => `/assistants/${sessionId}`
  ),
  assistantsViewTab: createAppRoute<{ sessionId: Uuid; tab: string }>(
    ({ sessionId, tab }) => `/assistants/${sessionId}/${tab}`
  ),
  assistantThreadsView: createAppRoute<{ threadId: Uuid }>(
    ({ threadId }) => `/assistant-threads/${threadId}`
  ),
  assistantRunsEvents: createAppRoute<{ assistantRunId: Uuid }>(
    ({ assistantRunId }) => `/assistant-runs/${assistantRunId}/events`
  ),

  /**
   * API Request Records
   */
  apiRequestRecords: createAppRoute(`/api-request-records`),
  apiRequestRecordsView: createAppRoute<{ apiRequestRecordId: Uuid }>(
    ({ apiRequestRecordId }) => `/api-request-records/${apiRequestRecordId}`
  ),

  /**
   * Captured Audio Routes
   */
  capturedAudio: createAppRoute(`/utilities/captured-audio`),
  capturedAudioView: createAppRoute<{ capturedAudioId: Uuid }>(
    ({ capturedAudioId }) => `/utilities/captured-audio/${capturedAudioId}`
  ),

  /**
   * Audio Recording Routes
   */
  audioRecordings: createAppRoute(`/audio-recordings`),
  audioRecordingsCreate: createAppRoute(`/audio-recordings/create`),
  audioRecordingsQualityMetrics: createAppRoute(`/audio-recordings/quality-metrics`),
  audioRecordingsView: createAppRoute<{ audioRecordingId: Uuid }>(
    ({ audioRecordingId }) => `/audio-recordings/${audioRecordingId}`
  ),
  audioRecordingsRecorder: createAppRoute<{ audioRecordingId: Uuid }>(
    ({ audioRecordingId }) => `/audio-recordings/${audioRecordingId}/recorder`
  ),

  /**
   * Note Suggestion Routes
   */
  noteSuggestions: createAppRoute(`/note-suggestions`),
  noteSuggestionsView: createAppRoute<{ noteSuggestionId: Uuid }>(
    ({ noteSuggestionId }) => `/note-suggestions/${noteSuggestionId}`
  ),

  /**
   * Captured Email Routes
   */
  capturedEmails: createAppRoute(`/utilities/captured-emails`),
  capturedEmailsView: createAppRoute<{ capturedEmailId: Uuid }>(
    ({ capturedEmailId }) => `/utilities/captured-emails/${capturedEmailId}`
  ),

  /**
   * SyncClientGroup Routes
   */
  syncClientGroups: createAppRoute(`/sync/sync-client-groups`),
  syncClientGroupsView: createAppRoute<{ syncClientGroupId: Uuid }>(
    ({ syncClientGroupId }) => `/sync/sync-client-groups/${syncClientGroupId}`
  ),

  /**
   * SyncCvr Routes
   */
  syncCvrs: createAppRoute(`/sync/sync-cvrs`),
  syncCvrsView: createAppRoute<{ syncCvrId: Uuid }>(
    ({ syncCvrId }) => `/sync/sync-cvrs/${syncCvrId}`
  ),

  /**
   * SyncModelCvrSnapshot Routes
   */
  syncModelCvrSnapshots: createAppRoute(`/sync/sync-model-cvr-snapshots`),
  syncModelCvrSnapshotsView: createAppRoute<{ syncModelCvrSnapshotId: Uuid }>(
    ({ syncModelCvrSnapshotId }) => `/sync/sync-model-cvr-snapshots/${syncModelCvrSnapshotId}`
  ),

  /**
   * SyncModelEvent Routes
   */
  syncModelEvents: createAppRoute(`/sync/sync-model-events`),
  syncModelEventsView: createAppRoute<{ syncModelEventId: Uuid }>(
    ({ syncModelEventId }) => `/sync/sync-model-events/${syncModelEventId}`
  ),

  /**
   * SyncScope Routes
   */
  syncScopes: createAppRoute(`/sync/sync-scopes`),
  syncScopesView: createAppRoute<{ syncScopeId: Uuid }>(
    ({ syncScopeId }) => `/sync/sync-scopes/${syncScopeId}`
  ),

  /**
   * SyncOperation Routes
   */
  syncOperations: createAppRoute(`/sync/sync-operations`),
  syncOperationsView: createAppRoute<{ syncOperationId: Uuid }>(
    ({ syncOperationId }) => `/sync/sync-operations/${syncOperationId}`
  ),

  /**
   * Data Import Routes
   */
  dataImports: createAppRoute(`/data-imports`),
  dataImportsView: createAppRoute<{ dataImportId: Uuid }>(
    ({ dataImportId }) => `/data-imports/${dataImportId}`
  ),
  dataImportDataImportItems: createAppRoute<{ dataImportId: Uuid }>(
    ({ dataImportId }) => `/data-imports/${dataImportId}/data-import-items`
  ),
  /**
   * Data Export Routes
   */
  dataExports: createAppRoute(`/data-exports`),
  dataExportsCreate: createAppRoute(`/data-exports/create`),
  dataExportsView: createAppRoute<{ dataExportId: Uuid }>(
    ({ dataExportId }) => `/data-exports/${dataExportId}`
  ),

  /**
   * Data Import Item Routes
   */
  dataImportItems: createAppRoute(`/data-import-items`),
  dataImportItemsView: createAppRoute<{ dataImportItemId: Uuid }>(
    ({ dataImportItemId }) => `/data-import-items/${dataImportItemId}`
  ),

  /**
   * Account Transition Info Routes
   */
  accountTransitionInfo: createAppRoute(`/account-transition-info`),
  accountTransitionInfoView: createAppRoute<{ accountTransitionInfoId: Uuid }>(
    ({ accountTransitionInfoId }) => `/account-transition-info/${accountTransitionInfoId}`
  ),

  /**
   * App Callouts
   */
  appCallouts: createAppRoute(`/app-callouts`),
  appCalloutsView: createAppRoute<{ appCalloutId: Uuid }>(
    ({ appCalloutId }) => `/app-callouts/${appCalloutId}`
  ),

  /**
   * Space Account App Callouts
   */
  spaceAccountAppCallouts: createAppRoute(`/space-account-app-callouts`),
  spaceAccountAppCalloutsView: createAppRoute<{ spaceAccountAppCalloutId: Uuid }>(
    ({ spaceAccountAppCalloutId }) => `/space-account-app-callouts/${spaceAccountAppCalloutId}`
  ),

  /**
   * BatchAccountUpgrade Routes
   */
  batchAccountUpgrades: createAppRoute(`/v1-batch-account-upgrade`),
  batchAccountUpgradeCreate: createAppRoute(`/v1-batch-account-upgrade/create`),
  batchAccountUpgradesView: createAppRoute<{ batchAccountUpgradeId: Uuid }>(
    ({ batchAccountUpgradeId }) => `/v1-batch-account-upgrade/${batchAccountUpgradeId}`
  ),

  /**
   * Import Session Routes
   */
  imports: createAppRoute(`/imports`),
  importsDashboard: createAppRoute(`/imports/dashboard`),
  importsView: createAppRoute<{ importSessionId: Uuid }>(
    ({ importSessionId }) => `/imports/${importSessionId}`
  ),

  /**
   * Utilities Routes
   */
  uploadedFiles: createAppRoute(`/utilities/uploaded-files`),
  uploadedFilesCreate: createAppRoute(`/utilities/uploaded-files/create`),
  uploadedFilesView: createAppRoute<{ uploadedFileId: Uuid }>(
    ({ uploadedFileId }) => `/utilities/uploaded-files/${uploadedFileId}`
  ),
  capturedWebsites: createAppRoute(`/utilities/captured-websites`),
  capturedWebsitesCreate: createAppRoute(`/utilities/captured-websites/create`),
  capturedWebsitesView: createAppRoute<{ capturedWebsiteId: Uuid }>(
    ({ capturedWebsiteId }) => `/utilities/captured-websites/${capturedWebsiteId}`
  ),
  webClips: createAppRoute(`/utilities/web-clips`),
  webClipsCreate: createAppRoute(`/utilities/web-clips/create`),
  webClipsView: createAppRoute<{ webClipId: Uuid }>(
    ({ webClipId }) => `/utilities/web-clips/${webClipId}`
  ),
  smartViews: createAppRoute(`/utilities/smart-views`),
  smartViewsGetOrGenerate: createAppRoute(`/utilities/smart-views/get-or-generate`),
  smartViewsView: createAppRoute<{ smartViewId: Uuid }>(
    ({ smartViewId }) => `/utilities/smart-views/${smartViewId}`
  ),
  smartEngineRuns: createAppRoute(`/utilities/smart-engine-runs`),
  smartEngineRunsView: createAppRoute<{ smartEngineRunId: Uuid }>(
    ({ smartEngineRunId }) => `/utilities/smart-engine-runs/${smartEngineRunId}`
  ),
  sharedItemInvites: createAppRoute(`/utilities/shared-item-invites`),
  sharedItemInvitesCreate: createAppRoute(`/utilities/shared-item-invites/create`),
  sharedItemInvitesView: createAppRoute<{ inviteId: Uuid }>(
    ({ inviteId }) => `/utilities/shared-item-invites/${inviteId}`
  ),

  /**
   * App Events (Utilities)
   */
  appEvents: createAppRoute(`/utilities/app-events`),
  appEventsView: createAppRoute<{ appEventId: Uuid }>(
    ({ appEventId }) => `/utilities/app-events/${appEventId}`
  ),

  /**
   * Billing (Support)
   */
  billing: createAppRoute(`/billing`),
  billingView: createAppRoute<{ spaceAccountId: Uuid }>(
    ({ spaceAccountId }) => `/billing/${spaceAccountId}`
  ),

  /**
   * Admin Staff Routes
   */
  adminBadges: createAppRoute(`/admin/badges`),
  adminBadgesCreate: createAppRoute(`/admin/badges/create`),
  adminBadgesView: createAppRoute<{ badgeId: Uuid }>(({ badgeId }) => `/admin/badges/${badgeId}`),
  adminMemberBadges: createAppRoute(`/admin/member-badges`),
  adminMemberBadgesCreate: createAppRoute(`/admin/member-badges/create`),
  adminMemberBadgesView: createAppRoute<{ memberBadgeId: Uuid }>(
    ({ memberBadgeId }) => `/admin/member-badges/${memberBadgeId}`
  ),
  adminLinearDashboard: createAppRoute(`/admin/linear-dashboard`),
  monetization: createAppRoute(`/monetization`),

  /**
   * Company Staff Profile Routes
   */
  companyProfile: createAppRoute(`/company/profile`),
  companyTeamMember: createAppRoute<{ staffMemberId: Uuid }>(
    ({ staffMemberId }) => `/company/team/${staffMemberId}`
  ),

  /**
   * Notification Routes
   */
  notifications: createAppRoute(`/notifications`),

  /**
   * Electric Routes
   */
  electricSessions: createAppRoute(`/electric/sessions`),
  electricSessionEvents: createAppRoute(`/electric/session-events`),
} as const;
